<nav mat-tab-nav-bar [tabPanel]="tabPanel">
  @for (tab of tabs | visibleFinanceTabs; track tab) {
    <a mat-tab-link
      (click)="gotoTab(tab)"
      [active]="tab.link === activeTab.link">
      {{tab.title}}
    </a>
  }
</nav>
<mat-tab-nav-panel #tabPanel></mat-tab-nav-panel>

<section class="connections-page">
  <h1>Accounting Connections</h1>
  <p class="intro">Manage Wallit’s single QuickBooks Online accounting connection.</p>

  @if (loading) {
    <mat-card class="connection-card state-row">
      <mat-spinner diameter="28"></mat-spinner>
      <span>Loading connection status…</span>
    </mat-card>
  }

  @if (!loading) {
    <mat-card class="connection-card">
      <div class="connection-heading">
        <div>
          <h2>QuickBooks Online</h2>
          <span class="state-pill" [attr.data-state]="status.state">{{stateLabels[status.state]}}</span>
        </div>
        <mat-icon aria-hidden="true">account_balance</mat-icon>
      </div>
      @if (status.state === 'CONFIGURATION_UNAVAILABLE') {
        <div class="message warning">
          QuickBooks connection management is temporarily unavailable. No other Finance feature is affected.
        </div>
      }
      @if (status.state === 'DISCONNECTED') {
        <div class="message">
          No QuickBooks company is connected to this Wallit environment.
        </div>
      }
      @if (status.state === 'REMOVED') {
        <div class="message">
          The previous QuickBooks connection was removed.
        </div>
      }
      @if (status.state === 'AUTHORIZATION_REQUIRES_REPLACEMENT') {
        <div class="message warning">
          Authorization is no longer usable. Remove this connection, then add it again. Company switching and reconnect are not available.
        </div>
      }
      @if (status.state === 'REFRESH_RECONCILIATION_REQUIRED') {
        <div class="message warning">
          QuickBooks token refresh could not be confirmed. Remove this connection, then add it again.
        </div>
      }
      @if (status.state === 'REVOCATION_UNKNOWN' || status.cleanupRequired) {
        <div class="message warning">
          Intuit token revocation could not be confirmed. The retained encrypted credentials are quarantined and cannot be used for ordinary QuickBooks requests. Use explicit cleanup to retry once.
        </div>
      }
      @if (status.company) {
        <dl class="company-details">
          <div>
            <dt>Company</dt>
            <dd>{{status.company.companyName || 'Not provided'}}</dd>
          </div>
          <div>
            <dt>Legal name</dt>
            <dd>{{status.company.legalName || 'Not provided'}}</dd>
          </div>
          <div>
            <dt>Country</dt>
            <dd>{{status.company.country || 'Not provided'}}</dd>
          </div>
          <div>
            <dt>Verified</dt>
            <dd>{{status.company.verifiedAt | date:'medium'}}</dd>
          </div>
        </dl>
      }
      <div class="actions">
        @if (status.actions.canConnect) {
          <button mat-raised-button color="primary"
            [disabled]="starting"
            (click)="connect()">
            <mat-icon>add_link</mat-icon>
            {{starting ? 'Starting connection…' : 'Connect QuickBooks'}}
          </button>
        }
        @if (status.actions.canRemove) {
          <button mat-stroked-button color="warn"
            [disabled]="removing"
            (click)="remove()">
            <mat-icon>link_off</mat-icon>
            {{removing ? 'Removing…' : 'Remove connection'}}
          </button>
        }
        @if (status.actions.canRetryCleanup) {
          <button mat-stroked-button
            [disabled]="cleaningUp"
            (click)="retryCleanup()">
            <mat-icon>cleaning_services</mat-icon>
            {{cleaningUp ? 'Retrying cleanup…' : 'Retry cleanup'}}
          </button>
        }
      </div>
    </mat-card>
  }

  @if (wallitProjection || wallitError) {
    <section class="comparison-section">
      <div class="comparison-heading">
        <button type="button"
          class="comparison-toggle"
          aria-controls="account-comparison-content account-qbo-comparison-content"
          [attr.aria-expanded]="isComparisonSectionExpanded('account')"
          (click)="toggleComparisonSection('account')">
          <span>
            <strong>Read-only Chart of Accounts comparison</strong>
            <small>Compare Wallit's active Finance accounts with QuickBooks. Matches are advisory only; no mapping or account changes are made.</small>
          </span>
          <mat-icon aria-hidden="true">{{isComparisonSectionExpanded('account') ? 'expand_less' : 'expand_more'}}</mat-icon>
        </button>
        @if (!loading && status.state === 'CONNECTED_VERIFIED') {
          <button mat-raised-button color="primary"
            [hidden]="!isComparisonSectionExpanded('account')"
            [disabled]="catalogLoading"
            (click)="loadAccountCatalog()">
            <mat-icon>download</mat-icon>
            {{catalogLoading ? 'Loading complete catalog…' : 'Load QuickBooks accounts'}}
          </button>
        }
      </div>
      <mat-card id="account-comparison-content"
        class="comparison-card wallit-card"
        [hidden]="!isComparisonSectionExpanded('account')">
        <div class="card-heading">
          <div>
            <h3>Wallit Chart of Accounts</h3>
            @if (wallitProjection) {
              <p>All active non-root Finance accounts as of {{wallitProjection.projectionAsOfDate}}</p>
            }
          </div>
          @if (wallitProjection) {
            <span class="count-pill">{{wallitProjection.accounts.length}}</span>
          }
        </div>
        @if (wallitLoading) {
          <div class="state-row">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Loading the Wallit Chart of Accounts…</span>
          </div>
        }
        @if (wallitError) {
          <div class="message warning">
            The Wallit Chart of Accounts is temporarily unavailable. QuickBooks accounts were not requested.
          </div>
        }
        @if (!wallitLoading && wallitProjection && !wallitProjection.accounts.length) {
          <div class="message">
            No active non-root Finance accounts are available for comparison.
          </div>
        }
        @if (wallitProjection?.accounts?.length) {
          <div class="table-scroll">
            <table class="comparison-table">
              <thead>
                <tr>
                  <th>Wallit account</th>
                  <th>Class and parent</th>
                  <th>Comparison scope</th>
                  <th>Advisory hint</th>
                </tr>
              </thead>
              @for (group of wallitAccountGroups; track group) {
                <tbody>
                  <tr class="wallit-account-group-row">
                    <th colspan="4">
                      <button type="button"
                        class="wallit-account-group-toggle"
                        [attr.aria-controls]="'wallit-account-group-' + group.accountTypeCode"
                        [attr.aria-expanded]="isWallitAccountGroupExpanded(group.accountTypeCode)"
                        (click)="toggleWallitAccountGroup(group.accountTypeCode)">
                        <span>
                          <strong>{{group.label}}</strong>
                          <small>{{group.accounts.length}} account{{group.accounts.length === 1 ? '' : 's'}}</small>
                        </span>
                        <mat-icon aria-hidden="true">
                          {{isWallitAccountGroupExpanded(group.accountTypeCode) ? 'expand_less' : 'expand_more'}}
                        </mat-icon>
                      </button>
                    </th>
                  </tr>
                </tbody>
                <tbody [id]="'wallit-account-group-' + group.accountTypeCode"
                  [hidden]="!isWallitAccountGroupExpanded(group.accountTypeCode)">
                  @for (account of group.accounts; track account) {
                    <tr>
                      <td>
                        <strong>{{account.name}}</strong>
                        <span>Code: {{account.code}}</span>
                        @if (account.isSystem) {
                          <small class="system-label">System account</small>
                        }
                      </td>
                      <td>
                        <span>{{account.accountTypeCode | titlecase}}</span>
                        <small>Parent: {{account.parent ? (account.parent.code + ' — ' + account.parent.name) : 'None'}}</small>
                      </td>
                      <td>
                        @for (reason of account.eligibilityReasons; track reason) {
                          <span class="evidence-chip">
                            {{eligibilityLabels[reason]}}
                          </span>
                        }
                      </td>
                      <td>
                        @if (hintFor(account); as hint) {
                          <span class="hint-pill" [attr.data-hint]="hint.result">{{hintLabels[hint.result]}}</span>
                          @for (quickBooksAccount of quickBooksAccountsFor(hint); track quickBooksAccount) {
                            <small>
                              {{quickBooksAccount.fullyQualifiedName}}
                              ({{quickBooksAccount.active === true ? 'Active' : quickBooksAccount.active === false ? 'Inactive' : 'State unknown'}})
                            </small>
                          }
                          @if (hint.sharedQuickBooksAccountIds.length) {
                            <small class="shared-label">
                              Same QuickBooks Account is hinted for multiple Wallit accounts.
                            </small>
                          }
                        } @else {
                          <span class="muted">Load QuickBooks accounts to compare.</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              }
            </table>
          </div>
        }
        @if (wallitProjection?.diagnostics?.length) {
          <details class="diagnostics">
            <summary>{{wallitProjection?.diagnostics?.length}} projection diagnostic(s)</summary>
            <ul>
              @for (diagnostic of wallitProjection?.diagnostics; track diagnostic) {
                <li>
                  <code>{{diagnostic.reasonCode}}</code>
                  @if (diagnostic.contractId) {
                    <span>Contract {{diagnostic.contractId}}</span>
                  }
                  @if (diagnostic.metadataRole) {
                    <span>Role {{diagnostic.metadataRole}}</span>
                  }
                  @if (diagnostic.accountId) {
                    <span>Account {{diagnostic.accountId}}</span>
                  }
                </li>
              }
            </ul>
          </details>
        }
      </mat-card>
      <mat-card id="account-qbo-comparison-content"
        class="comparison-card qbo-card"
        [hidden]="!isComparisonSectionExpanded('account')">
        <div class="card-heading">
          <div>
            <h3>QuickBooks Accounts</h3>
            @if (accountCatalog) {
              <p>Complete catalog retrieved {{accountCatalog.retrievedAt | date:'medium'}}</p>
            }
          </div>
          @if (accountCatalog) {
            <span class="count-pill">{{accountCatalog.accounts.length}}</span>
          }
        </div>
        @if (!accountCatalog && !catalogLoading && !catalogError) {
          <div class="message">
            QuickBooks has not been contacted. Select “Load QuickBooks accounts” to request the complete active and inactive catalog.
          </div>
        }
        @if (catalogLoading) {
          <div class="state-row">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Loading every active and inactive QuickBooks Account…</span>
          </div>
        }
        @if (catalogError) {
          <div class="message warning">{{catalogError}}</div>
        }
        @if (accountCatalog && !accountCatalog.accounts.length) {
          <div class="message">
            The complete QuickBooks Account catalog is empty.
          </div>
        }
        @if (accountCatalog?.accounts?.length) {
          <div class="table-scroll">
            <table class="comparison-table qbo-table">
              <thead>
                <tr>
                  <th>QuickBooks account</th>
                  <th>State</th>
                  <th>Classification</th>
                  <th>Type / subtype</th>
                  <th>Currency</th>
                </tr>
              </thead>
              <tbody>
                @for (row of quickBooksAccountRows; track row) {
                  <tr>
                    <td class="qbo-account-cell" [attr.aria-label]="row.account.fullyQualifiedName">
                      <div class="qbo-account-label" [style.padding-left.px]="row.depth * 24">
                        @if (row.depth > 0) {
                          <span class="qbo-hierarchy-marker" aria-hidden="true"></span>
                        }
                        <div>
                          <strong>{{row.account.name}}</strong>
                          <small>Account number: {{row.account.acctNum || 'Not provided'}}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{row.account.active === true ? 'Active' : row.account.active === false ? 'Inactive' : 'Unknown'}}</td>
                    <td>{{row.account.classification}}</td>
                    <td>{{row.account.accountType}} / {{row.account.accountSubType || 'Unknown'}}</td>
                    <td>{{currencyDisplay(row.account)}}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </mat-card>
    </section>
  }

  <section class="comparison-section party-comparison-section">
    <div class="comparison-heading">
      <button type="button"
        class="comparison-toggle"
        aria-controls="vendor-comparison-content"
        [attr.aria-expanded]="isComparisonSectionExpanded('vendor')"
        (click)="toggleComparisonSection('vendor')">
        <span>
          <strong>Read-only supplier / Vendor comparison</strong>
          <small>Wallit vendor rows and exact-name hints are provisional information only. No party is approved, mapped, or changed.</small>
        </span>
        <mat-icon aria-hidden="true">{{isComparisonSectionExpanded('vendor') ? 'expand_less' : 'expand_more'}}</mat-icon>
      </button>
      @if (!loading && status.state === 'CONNECTED_VERIFIED') {
        <button mat-raised-button color="primary"
          [hidden]="!isComparisonSectionExpanded('vendor')"
          [disabled]="vendorCatalogLoading"
          (click)="loadVendorCatalog()">
          <mat-icon>visibility</mat-icon>
          {{vendorCatalogLoading ? 'Loading complete Vendor catalog…' : 'Load QuickBooks Vendors'}}
        </button>
      }
    </div>

    <div id="vendor-comparison-content"
      class="party-card-grid"
      [hidden]="!isComparisonSectionExpanded('vendor')">
      <mat-card class="comparison-card wallit-card">
        <div class="card-heading">
          <div>
            <h3>Wallit supplier / carrier candidates</h3>
            <p>Source: the Wallit vendors representation. Operational keys are shown as stored; no lifecycle state is inferred.</p>
          </div>
          @if (supplierCandidates) {
            <span class="count-pill">{{supplierCandidates.suppliers.length}}</span>
          }
        </div>

        @if (supplierCandidatesLoading) {
          <div class="state-row">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Loading Wallit supplier candidates…</span>
          </div>
        }
        @if (supplierCandidatesError) {
          <div class="message warning">
            Wallit supplier candidates are temporarily unavailable. QuickBooks Vendors were not requested.
          </div>
        }
        @if (!supplierCandidatesLoading && supplierCandidates && !supplierCandidates.suppliers.length) {
          <div class="message">
            No Wallit supplier candidates are available.
          </div>
        }

        @if (supplierCandidates?.suppliers?.length) {
          <div class="table-scroll">
            <table class="comparison-table party-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Source / type</th>
                  <th>Advisory hint</th>
                </tr>
              </thead>
              <tbody>
                @for (candidate of supplierCandidates?.suppliers; track candidate) {
                  <tr>
                    <td>
                      <strong>{{candidate.displayName}}</strong>
                      <small>Operational key: {{candidate.operationalKey}}</small>
                      <small>Wallit source ID: {{candidate.sourceId}}</small>
                    </td>
                    <td><span class="source-pill">Wallit / Supplier candidate</span></td>
                    <td>
                      @if (vendorHintFor(candidate); as hint) {
                        <span class="hint-pill" [attr.data-hint]="hint.result">{{partyHintLabels[hint.result]}}</span>
                        @for (vendor of vendorsFor(hint); track vendor) {
                          <small>
                            QuickBooks Vendor: {{vendor.displayName}} ({{vendor.active ? 'Active' : 'Inactive'}})
                          </small>
                        }
                        @if (hint.duplicateWallitSourceIds.length > 1) {
                          <small class="shared-label">
                            {{hint.duplicateWallitSourceIds.length}} Wallit rows share this normalized name.
                          </small>
                        }
                        @if (hint.duplicateQuickBooksEntityIds.length) {
                          <small class="shared-label">
                            Multiple QuickBooks Vendors share this normalized name; no result is selected.
                          </small>
                        }
                        @if (hasPossibleRoleOverlap(candidate.displayName)) {
                          <small class="shared-label">
                            Possible role overlap: the same normalized name exists in both QuickBooks catalogs.
                          </small>
                        }
                      } @else {
                        <span class="muted">Load QuickBooks Vendors to compare.</span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </mat-card>

      <mat-card class="comparison-card qbo-card">
        <div class="card-heading">
          <div>
            <h3>QuickBooks Vendors</h3>
            @if (vendorCatalog) {
              <p>Complete catalog retrieved {{vendorCatalog.retrievedAt | date:'medium'}}</p>
            }
          </div>
          @if (vendorCatalog) {
            <span class="count-pill">{{vendorCatalog.vendors.length}}</span>
          }
        </div>
        @if (!vendorCatalog && !vendorCatalogLoading && !vendorCatalogError) {
          <div class="message">
            QuickBooks has not been contacted for Vendors. Use the separate Vendor load action above.
          </div>
        }
        @if (vendorCatalogLoading) {
          <div class="state-row">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Loading every active and inactive QuickBooks Vendor…</span>
          </div>
        }
        @if (vendorCatalogError) {
          <div class="message warning">{{vendorCatalogError}}</div>
        }
        @if (vendorCatalog && !vendorCatalog.vendors.length) {
          <div class="message">
            The complete QuickBooks Vendor catalog is empty.
          </div>
        }
        @if (vendorCatalog?.vendors?.length) {
          <div class="table-scroll">
            <table class="comparison-table party-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Source / type</th>
                  <th>State</th>
                  <th>Last updated</th>
                </tr>
              </thead>
              <tbody>
                @for (vendor of vendorCatalog?.vendors; track vendor) {
                  <tr>
                    <td>
                      <strong>{{vendor.displayName}}</strong>
                      @if (vendor.companyName) {
                        <small>Company: {{vendor.companyName}}</small>
                      }
                    </td>
                    <td><span class="source-pill">QuickBooks Online / Vendor</span></td>
                    <td><span class="state-pill compact" [attr.data-active]="vendor.active">{{vendor.active ? 'Active' : 'Inactive'}}</span></td>
                    <td>{{vendor.lastUpdatedAt ? (vendor.lastUpdatedAt | date:'medium') : 'Not provided'}}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </mat-card>
    </div>
  </section>

  <section class="comparison-section party-comparison-section">
    <div class="comparison-heading">
      <button type="button"
        class="comparison-toggle"
        aria-controls="customer-comparison-content"
        [attr.aria-expanded]="isComparisonSectionExpanded('customer')"
        (click)="toggleComparisonSection('customer')">
        <span>
          <strong>Read-only employer / Customer comparison</strong>
          <small>Wallit company rows and exact-name hints are provisional information only. No party is approved, mapped, or changed.</small>
        </span>
        <mat-icon aria-hidden="true">{{isComparisonSectionExpanded('customer') ? 'expand_less' : 'expand_more'}}</mat-icon>
      </button>
      @if (!loading && status.state === 'CONNECTED_VERIFIED') {
        <button mat-raised-button color="primary"
          [hidden]="!isComparisonSectionExpanded('customer')"
          [disabled]="customerCatalogLoading"
          (click)="loadCustomerCatalog()">
          <mat-icon>visibility</mat-icon>
          {{customerCatalogLoading ? 'Loading complete Customer catalog…' : 'Load QuickBooks Customers'}}
        </button>
      }
    </div>

    <div id="customer-comparison-content"
      class="party-card-grid"
      [hidden]="!isComparisonSectionExpanded('customer')">
      <mat-card class="comparison-card wallit-card">
        <div class="card-heading">
          <div>
            <h3>Wallit employer / client candidates</h3>
            <p>Source: non-deleted Wallit teamcompanies rows. Private company details are not loaded.</p>
          </div>
          @if (companyCandidates) {
            <span class="count-pill">{{companyCandidates.companies.length}}</span>
          }
        </div>
        @if (companyCandidatesLoading) {
          <div class="state-row">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Loading Wallit company candidates…</span>
          </div>
        }
        @if (companyCandidatesError) {
          <div class="message warning">
            Wallit company candidates are temporarily unavailable. QuickBooks Customers were not requested.
          </div>
        }
        @if (!companyCandidatesLoading && companyCandidates && !companyCandidates.companies.length) {
          <div class="message">
            No Wallit company candidates are available.
          </div>
        }
        @if (companyCandidates?.companies?.length) {
          <div class="table-scroll">
            <table class="comparison-table party-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Source / type</th>
                  <th>Advisory hint</th>
                </tr>
              </thead>
              <tbody>
                @for (candidate of companyCandidates?.companies; track candidate) {
                  <tr>
                    <td>
                      <strong>{{candidate.displayName}}</strong>
                      <small>Classification: {{candidate.classification || 'Not provided'}}</small>
                      <small>Wallit source ID: {{candidate.sourceId}}</small>
                    </td>
                    <td><span class="source-pill">Wallit / Company candidate</span></td>
                    <td>
                      @if (customerHintFor(candidate); as hint) {
                        <span class="hint-pill" [attr.data-hint]="hint.result">{{partyHintLabels[hint.result]}}</span>
                        @for (customer of customersFor(hint); track customer) {
                          <small>
                            QuickBooks Customer: {{customer.displayName}} ({{customer.active ? 'Active' : 'Inactive'}})
                          </small>
                        }
                        @if (hint.duplicateWallitSourceIds.length > 1) {
                          <small class="shared-label">
                            {{hint.duplicateWallitSourceIds.length}} Wallit rows share this normalized name.
                          </small>
                        }
                        @if (hint.duplicateQuickBooksEntityIds.length) {
                          <small class="shared-label">
                            Multiple QuickBooks Customers share this normalized name; no result is selected.
                          </small>
                        }
                        @if (hasPossibleRoleOverlap(candidate.displayName)) {
                          <small class="shared-label">
                            Possible role overlap: the same normalized name exists in both QuickBooks catalogs.
                          </small>
                        }
                      } @else {
                        <span class="muted">Load QuickBooks Customers to compare.</span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </mat-card>

      <mat-card class="comparison-card qbo-card">
        <div class="card-heading">
          <div>
            <h3>QuickBooks Customers</h3>
            @if (customerCatalog) {
              <p>Complete catalog retrieved {{customerCatalog.retrievedAt | date:'medium'}}</p>
            }
          </div>
          @if (customerCatalog) {
            <span class="count-pill">{{customerCatalog.customers.length}}</span>
          }
        </div>
        @if (!customerCatalog && !customerCatalogLoading && !customerCatalogError) {
          <div class="message">
            QuickBooks has not been contacted for Customers. Use the separate Customer load action above.
          </div>
        }
        @if (customerCatalogLoading) {
          <div class="state-row">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Loading every active and inactive QuickBooks Customer…</span>
          </div>
        }
        @if (customerCatalogError) {
          <div class="message warning">{{customerCatalogError}}</div>
        }
        @if (customerCatalog && !customerCatalog.customers.length) {
          <div class="message">
            The complete QuickBooks Customer catalog is empty.
          </div>
        }
        @if (customerCatalog?.customers?.length) {
          <div class="table-scroll">
            <table class="comparison-table party-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Source / type</th>
                  <th>State</th>
                  <th>Hierarchy</th>
                  <th>Last updated</th>
                </tr>
              </thead>
              <tbody>
                @for (customer of customerCatalog?.customers; track customer) {
                  <tr>
                    <td>
                      <strong>{{customer.displayName}}</strong>
                      @if (customer.companyName) {
                        <small>Company: {{customer.companyName}}</small>
                      }
                    </td>
                    <td><span class="source-pill">QuickBooks Online / Customer</span></td>
                    <td><span class="state-pill compact" [attr.data-active]="customer.active">{{customer.active ? 'Active' : 'Inactive'}}</span></td>
                    <td>
                      <span>{{customer.fullyQualifiedName || 'Not provided'}}</span>
                      @if (customer.parentRef) {
                        <small>Parent: {{customer.parentRef.displayName || customer.parentRef.id}}</small>
                      }
                    </td>
                    <td>{{customer.lastUpdatedAt ? (customer.lastUpdatedAt | date:'medium') : 'Not provided'}}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </mat-card>
    </div>
  </section>
</section>
